import api from '@app/api'

import { CONTENT_TYPES, FILE_TYPES } from '@app/api/constants'

import type { ResponseDto } from '@app/api/dtos'
import type { QuotaDirectoriesDto, QuotaReportsDto } from './dtos'
import type {
  CreateQuotaData,
  GetQuotaParams,
  DeleteQuotaData,
  CopyQuotaData,
  ExportExcel,
  PatchQuotaData,
  ExportAllQuotaReportsParams
} from './types'
import type { ObjectValuesType } from '@app/types'

const URL = '/quotas/'

export const getQuotaReports = async (params?: GetQuotaParams): Promise<ResponseDto<QuotaReportsDto>> => {
  const response = await api.get<ResponseDto<QuotaReportsDto>>(`${URL}filling/`, { params })
  return response.data
}

export const getQuotaDirectories = async (params?: GetQuotaParams): Promise<ResponseDto<QuotaDirectoriesDto>> => {
  const response = await api.get<ResponseDto<QuotaDirectoriesDto>>(URL, { params })
  return response.data
}

export const createQuota = async (data: CreateQuotaData): Promise<void> => {
  await api.post(URL, data)
}

export const patchQuota = async ({ id, ...data }: PatchQuotaData): Promise<void> => {
  await api.patch<QuotaReportsDto>(`${URL}${id}/`, data)
}

export const deleteQuota = async (data: DeleteQuotaData): Promise<void> => {
  await api.delete(URL, { data })
}

export const copyQuota = async ({ id, ...data }: CopyQuotaData): Promise<void> => {
  await api.post(`${URL}${id}/copy/`, data)
}

export const exportQuotas = async (params: ExportAllQuotaReportsParams, signal?: AbortSignal): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}export/filters/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })

  return response.data
}

export const exportQuotaReports = async (params: ExportAllQuotaReportsParams, signal?: AbortSignal): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}report/filters/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })

  return response.data
}

export const exportQuota = async (params: ExportExcel, signal?: AbortSignal): Promise<Blob> => {
  const { ids, file } = params

  const url = `${URL}export/ids/`

  const response = await api.post<Blob>(url, { file, ids }, { signal, responseType: 'blob' })

  return response.data
}

export const exportQuotaReport = async (params: ExportExcel, signal?: AbortSignal): Promise<Blob> => {
  const { ids, file } = params

  const url = `${URL}report/ids/`

  const response = await api.post<Blob>(url, { file, ids }, { signal, responseType: 'blob' })

  return response.data
}

export const exportTemplateQuotaReport = async (
  params: {
    file: ObjectValuesType<typeof FILE_TYPES>
  },
  signal?: AbortSignal
): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}export/template/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })

  return response.data
}

export const importQuotaReportFromFile = async (data: FormData): Promise<void> => {
  await api.post(`${URL}import/`, data, {
    headers: {
      'Content-Type': CONTENT_TYPES.multipartFormData
    }
  })
}
